// Root component of the app.

import { type FunctionComponent, useEffect } from 'react';

import { Helmet } from '@dr.pogodin/react-helmet';
import { MetaTags, ThemeProvider } from '@dr.pogodin/react-utils';

import { RenderErrorPage } from 'pages/RenderErrorPage';

import Routes from 'routes';

import { useGlobalState } from 'utils';

import favIconAppleTouch from 'assets/images/favicons/apple-touch-icon.png';
import favIcon16 from 'assets/images/favicons/favicon-16x16.png';

import favIcon32 from 'assets/images/favicons/favicon-32x32.png';

import primaryButtonTheme from 'themes/Button/primary-40px.scss';
import defaultCheckboxTheme from 'themes/Checkbox/default.scss';
import defaultDropdownTheme from 'themes/Dropdown/default.scss';
import defaultInputTheme from 'themes/Input/default-56px.scss';

const Application: FunctionComponent = () => {
  const [webApp] = useGlobalState('webApp');

  // NOTE: The screen wake lock request is done this complex way because on iOS
  // it will be denied if the user has not clicked on the page yet. Yes, "click"
  // event is not ideal here, but "mousedown" or "pointerdown" do not work for
  // it on iOS (it will still deny the request unless).
  useEffect(() => {
    function listener() {
      void navigator.wakeLock.request('screen');
      document.removeEventListener('click', listener);
    }
    if (webApp === 'Primary') {
      document.addEventListener('click', listener);
    }
    return () => {
      document.removeEventListener('click', listener);
    };
  }, [webApp]);

  return (
    <ThemeProvider
      themes={{
        Button: primaryButtonTheme,
        Checkbox: defaultCheckboxTheme,
        Dropdown: defaultDropdownTheme,
        Input: defaultInputTheme,
      }}
    >
      <RenderErrorPage>
        <Helmet>
          <link href={favIconAppleTouch} rel="apple-touch-icon" sizes="180x180" />
          <link href={favIcon32} rel="icon" sizes="32x32" type="image/png" />
          <link href={favIcon16} rel="icon" sizes="16x16" type="image/png" />
        </Helmet>
        <MetaTags
          description="Soul Sanctuary uses intelligent technology and expert-led practices to help you change your state, prepare, recover, and perform at your best."
          title="Soul Sanctuary - Personalized State Transformation"
        >
          <Routes />
        </MetaTags>
      </RenderErrorPage>
    </ThemeProvider>
  );
};

export default Application;
